// Day or night look for the signed-in pages.
//
// The choice is per person and per browser (localStorage), like the driver
// app: Light, Dark, or Same as computer. Night mode works by putting the
// `lm-night` class on <html> while a signed-in page is open; the overrides in
// styles/appTheme.css do the rest, so no page has to know about it. The class
// sits on <html> rather than the layout so modals rendered into <body> follow
// it too, and it is removed when the layout unmounts so public pages never see it.
import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'loadmemo.appearance';
const MODES = ['light', 'dark', 'system'];

const readMode = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const AppearanceContext = createContext({ mode: 'system', isNight: false, setMode: () => {} });
export const useAppearance = () => useContext(AppearanceContext);

export const AppearanceProvider = ({ children }) => {
  const [mode, setModeState] = useState(readMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const isNight = mode === 'dark' || (mode === 'system' && systemDark);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('lm-night', isNight);
    return () => root.classList.remove('lm-night');
  }, [isNight]);

  const setMode = (next) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private window: keep it for this visit */ }
  };

  return (
    <AppearanceContext.Provider value={{ mode, isNight, setMode }}>
      {children}
    </AppearanceContext.Provider>
  );
};
